import { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import { AsyncSelect } from 'common-ui/select';
import dayjs from 'dayjs';
import { getLeadSources } from 'features/sales/salesActions';
import { generateOptions } from 'utils';

import { ButtonWrapper, FilterContainer, FilterWrapper, SelectWrapper } from './Filter.styles';
import { useReportSearchParams } from './useSearchData';

const getFilterCount = (searchData) => {
  const filterKeys = ['period', 'endDate', 'leadSourceId', 'statusId', 'offerId', 'creatorId'];

  return filterKeys.reduce((count, key) => {
    const value = searchData[key];
    if (key === 'startDate' || key === 'endDate') {
      return count + (value ? 1 : 0);
    }
    return count + (value !== null && value !== undefined && value !== '' ? 1 : 0);
  }, 0);
};

const Filter = () => {
  const dispatch = useDispatch();
  const [showFilter, setShowFilter] = useState(false);
  const [leadSources, setLeadSources] = useState([]);

  const { searchData, setReportSearchData } = useReportSearchParams();
  const searchCount = getFilterCount(searchData);

  const handleClickShowHideSearch = () => {
    setShowFilter(!showFilter);
  };

  const handleLoadLeadSources = useCallback(
    async (search = '') => {
      try {
        const params = { limit: 10000 };
        if (search) params.search = search;

        const response = await dispatch(getLeadSources(params)).unwrap();
        const options = generateOptions(response.leadSources || []);
        setLeadSources(options);
        return options;
      } catch (error) {
        return [];
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (searchData.leadSourceId) {
      handleLoadLeadSources();
    }
  }, [searchData.leadSourceId]);

  const handleDateChange = (date, start) => {
    const parsedDate = new Date(date);
    parsedDate.setHours(0, 0, 0, 0);
    if (start) {
      parsedDate.setHours(23, 59, 59, 999);
    }
    const iso = date ? parsedDate.toISOString() : null;
    return iso;
  };

  return (
    <FilterContainer>
      <ButtonWrapper>
        <Button
          outlined
          onClick={handleClickShowHideSearch}
          className="action-button"
          active={searchCount > 0}
          variant="filter"
        >
          Filter{searchCount > 0 ? ` (${searchCount})` : ''}
        </Button>
      </ButtonWrapper>
      {showFilter && (
        <FilterWrapper>
          <SelectWrapper>
            <CustomDatePicker
              label="Date Range"
              placeholder="Select start date"
              value={searchData.startDate ? dayjs(searchData.startDate) : null}
              onChange={(date) =>
                setReportSearchData({ startDate: handleDateChange(date, true) })
              }
              disableFuture
              maxDate={searchData.endDate ? dayjs(searchData.endDate) : null}
              clearable={false}
            />
          </SelectWrapper>
          <SelectWrapper>
            <CustomDatePicker
              label="‎"
              placeholder="Select end date"
              value={searchData.endDate ? dayjs(searchData.endDate) : null}
              disableFuture
              onChange={(date) => setReportSearchData({ endDate: handleDateChange(date) })}
              minDate={searchData.startDate ? dayjs(searchData.startDate) : null}
              clearable
            />
          </SelectWrapper>
          <SelectWrapper>
            <AsyncSelect
              label="Lead Source"
              placeholder="Lead source"
              minHeight="38px"
              loadOptions={handleLoadLeadSources}
              onMenuOpen={handleLoadLeadSources}
              maxHeight="38px"
              defaultOptions={leadSources}
              value={
                searchData.leadSourceId
                  ? leadSources.find((source) => source.value === searchData.leadSourceId)
                  : null
              }
              onChange={(option) =>
                setReportSearchData({ leadSourceId: option?.value || null })
              }
              isClearable
            />
          </SelectWrapper>
        </FilterWrapper>
      )}
    </FilterContainer>
  );
};

export default Filter;
